import { onBeforeUnmount, type Ref, ref, unref, watch } from "vue";
import {
  type AnchoredPositionStyle,
  type Placement,
  positionForAnchor,
  trackAnchor
} from "@/lib/anchoredPosition";

type MaybeRef<T> = T | Ref<T>;

interface UseAnchoredDropdownOptions {
  /** The trigger the panel is anchored to. */
  anchor: Ref<HTMLElement | null>;
  isOpen: Ref<boolean>;
  /**
   * True when the panel is teleported out of the flow and needs fixed
   * coordinates. When false only `placement`/`maxHeight` are computed — an
   * in-flow panel already tracks its trigger on its own.
   */
  positioned?: MaybeRef<boolean>;
  offset?: MaybeRef<number>;
  preferredHeight?: MaybeRef<number>;
  /** Room the panel's current side must keep before it may switch sides. */
  minUsableHeight?: MaybeRef<number>;
  /** Called when the trigger scrolls out of its clipping ancestor. */
  onAnchorHidden?: () => void;
}

/**
 * Keeps a dropdown panel anchored to its trigger while the page scrolls or the
 * visible viewport changes, and flips it above the trigger when there isn't
 * room below. `trackAnchor` says what triggers a re-measure. A caller that is
 * already open at setup (a panel mounted with v-if) is positioned right away.
 */
export const useAnchoredDropdown = ({
  anchor,
  isOpen,
  positioned = true,
  offset = 1,
  preferredHeight = 320,
  minUsableHeight,
  onAnchorHidden
}: UseAnchoredDropdownOptions) => {
  const floatingStyle = ref<AnchoredPositionStyle | null>(null);
  const placement = ref<Placement>("bottom");
  const maxHeight = ref<number | null>(null);

  let stopTracking: (() => void) | null = null;
  // The side the open panel settled on; null until the first measurement, so
  // opening decides afresh and every re-measure after that has memory.
  let settled: Placement | null = null;
  // A trigger that looks hidden is measured again on the next frame before the
  // panel is dismissed. It may sit in a popover that repositions itself from
  // its own frame callback on the same resize (a tippy, via Popper), and which
  // callback runs first is not ours to choose. Measured first, the trigger is
  // still at its pre-resize place off the narrowed window, and looks scrolled
  // away for that one frame.
  let hiddenRecheck: number | null = null;

  const cancelHiddenRecheck = () => {
    if (hiddenRecheck !== null) cancelAnimationFrame(hiddenRecheck);
    hiddenRecheck = null;
  };

  const update = (confirmingHidden = false) => {
    if (!isOpen.value || !anchor.value) return;

    const result = positionForAnchor(anchor.value, {
      // A positioned panel is the teleported one, so it escapes the anchor's
      // clipping ancestors and is bounded only by the visible viewport.
      escapesClipping: Boolean(unref(positioned)),
      offset: unref(offset),
      preferredHeight: unref(preferredHeight),
      minUsableHeight: unref(minUsableHeight),
      currentPlacement: settled ?? undefined
    });

    if (!result.anchorVisible) {
      if (confirmingHidden) {
        onAnchorHidden?.();
      } else if (hiddenRecheck === null) {
        hiddenRecheck = requestAnimationFrame(() => {
          hiddenRecheck = null;
          update(true);
        });
      }
      return;
    }

    cancelHiddenRecheck();
    settled = result.placement;
    placement.value = result.placement;
    maxHeight.value = result.maxHeight;
    floatingStyle.value = unref(positioned) ? result.style : null;
  };

  const stop = () => {
    stopTracking?.();
    stopTracking = null;
    cancelHiddenRecheck();
  };

  watch(
    isOpen,
    (open) => {
      if (open && anchor.value) {
        settled = null;
        // Synchronous on purpose: only the anchor is measured, and it is already
        // laid out. Deferring to a frame would let the panel paint once before it
        // had coordinates, flashing at the foot of the document.
        update();
        stopTracking = trackAnchor(anchor.value, update);
      } else {
        stop();
        settled = null;
        floatingStyle.value = null;
        maxHeight.value = null;
        placement.value = "bottom";
      }
    },
    { immediate: true }
  );

  onBeforeUnmount(stop);

  return { floatingStyle, placement, maxHeight };
};
